// src/components/Separation/ExitChecklist.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from "sonner";
import { ExitChecklist as ExitChecklistType, ExitChecklistItem } from '../../types/separation';
import { getExitChecklist, updateChecklistItem, assignChecklist } from '../../api/separation';

interface ExitChecklistProps {
  separationType: string;
  separationId: string;
  isModal?: boolean;
  onClose?: () => void;
}

const ExitChecklist: React.FC<ExitChecklistProps> = ({ separationType, separationId, isModal = false, onClose }) => {
  const [checklist, setChecklist] = useState<ExitChecklistType | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getExitChecklist(separationType, separationId);
      setChecklist(response.data);
    } catch (error: any) {
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  }, [separationType, separationId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const handleToggleItem = async (item: ExitChecklistItem) => {
    if (!checklist) return;
    setUpdating(true);
    try {
      const response = await updateChecklistItem(checklist.id, {
        key: item.key,
        completed: !item.completed,
        notes: item.notes || undefined,
      });
      setChecklist(prev => prev ? { ...prev, items: response.items, status: response.status } : null);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const completedCount = checklist?.items?.filter(i => i.completed).length || 0;
  const totalCount = checklist?.items?.length || 0;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const content = (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">Exit Checklist</h3>
            <p className="text-sm text-gray-500 mt-1">
              {separationType} - {separationId.slice(0, 8)}...
            </p>
          </div>
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(checklist?.status || 'PENDING')}`}>
            {checklist?.status || 'PENDING'}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Progress</span>
            <span>{completedCount}/{totalCount} ({Math.round(progress)}%)</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {checklist?.items?.map((item) => (
          <div key={item.key} className="px-6 py-4 flex items-start gap-3 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={item.completed}
              onChange={() => handleToggleItem(item)}
              disabled={updating}
              className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <div className="flex-1">
              <label className="font-medium text-gray-900 cursor-pointer" onClick={() => handleToggleItem(item)}>
                {item.label}
              </label>
              {item.notes && <p className="text-sm text-gray-500 mt-1">{item.notes}</p>}
              {item.completed_by && (
                <p className="text-xs text-gray-400 mt-1">
                  Completed by: {item.completed_by} at {item.completed_at ? new Date(item.completed_at).toLocaleString() : ''}
                </p>
              )}
            </div>
            {item.completed && (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {isModal && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Close
          </button>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
          <div className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {content}
          </div>
        </div>
      </div>
    );
  }

  return content;
};

export default ExitChecklist;